import React from 'react';
import { LogoInstagram } from 'react-ionicons';
import { Link } from 'react-router-dom';

import { SUPPORT_EMAIL } from '../../constants/contact';
import { FOOTER_PRIVACY_LABELS } from '../../constants/cookieConsentCopy';
import { useCookieConsent } from '../../contexts/CookieConsentContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { FILLS, FONTS, GRADIENTS, RADII, SIZING, STROKES } from '../../styles/tokens';

interface SocialLink {
  id: string;
  label: string;
  href: string;
  Icon: typeof LogoInstagram;
}

/**
 * Social channel tiles rendered in Footer Tier 1. Adding a future channel
 * (e.g. Facebook, LinkedIn) is a one-array-entry change.
 */
export const SOCIAL_LINKS: SocialLink[] = [
  {
    id: 'instagram',
    label: 'Belhop on Instagram',
    href: 'https://www.instagram.com/thebelhopgroup/',
    Icon: LogoInstagram,
  },
];

const INTERNAL_LINKS = [
  { label: 'About Us', to: '/about' },
  { label: 'Support', to: '/support' },
  { label: 'Guides', to: '/blog' },
  { label: 'Join', to: '/join' },
  { label: 'Terms', to: '/terms' },
  { label: 'Privacy', to: '/privacy' },
] as const;

const internalLinkStyle: React.CSSProperties = {
  fontFamily: FONTS.body,
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  textDecoration: 'none',
};

const legalLineButtonStyle: React.CSSProperties = {
  fontFamily: FONTS.body,
  fontSize: '12px',
  textDecoration: 'none',
  background: 'none',
  border: 0,
  padding: 0,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

/**
 * Site-wide footer — "Variant B: The Lamp Line". Mounted once in `Layout`,
 * suppressed on `/admin/*`. Two tiers on `FILLS.surfaceRaised`, flat (no
 * shadow/blur), topped by a 1px gold-gradient hairline at 50% opacity — the
 * echo of the concierge rail's signature hairline. See DESIGN.md "Footer"
 * for the full spec.
 */
const Footer: React.FC = () => {
  const isMobile = useIsMobile();
  const { openPreferences } = useCookieConsent();
  const sidePadding = isMobile ? '24px' : 'max(48px, calc(50vw - 592px))';

  return (
    <footer
      style={{
        position: 'relative',
        background: FILLS.surfaceRaised,
        paddingLeft: sidePadding,
        paddingRight: sidePadding,
        paddingTop: '32px',
        paddingBottom: isMobile ? `calc(24px + ${SIZING.bottomNavOffset})` : '32px',
      }}
    >
      {/* Top edge hairline — echoes the concierge rail's signature gold hairline */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background: GRADIENTS.strokeGold,
          opacity: 0.5,
        }}
      />

      {/* Tier 1 */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'center' : 'flex-start',
          justifyContent: 'space-between',
          gap: isMobile ? '24px' : '16px',
          textAlign: isMobile ? 'center' : 'left',
        }}
      >
        {/* Left: logo lockup + locality micro-label */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: isMobile ? 'center' : 'flex-start',
            gap: '10px',
          }}
        >
          <img
            src="/assets/belhoplogo.png"
            alt="Belhop"
            style={{ height: '26px', display: 'block' }}
          />
          <span
            style={{
              fontFamily: FONTS.body,
              fontSize: '11px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.16em',
              color: FILLS.textMuted,
            }}
          >
            ASPEN <span style={{ color: FILLS.goldAlpha30 }}>&middot;</span> SNOWMASS
          </span>
        </div>

        {/* Right: internal links + social tiles */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: isMobile ? '16px' : '32px',
          }}
        >
          {INTERNAL_LINKS.map((link) => (
            <Link key={link.to} to={link.to} className="footer-link" style={internalLinkStyle}>
              {link.label}
            </Link>
          ))}

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {SOCIAL_LINKS.map(({ id, label, href, Icon }) => (
              <a
                key={id}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                className="footer-social-tile"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px',
                  borderRadius: RADII.iconBox,
                  border: `1px solid ${FILLS.goldAlpha30}`,
                }}
              >
                <Icon width="18px" height="18px" color={FILLS.gold} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Tier 2 */}
      <div
        style={{
          marginTop: '24px',
          paddingTop: '20px',
          borderTop: `1px solid ${STROKES.subtle}`,
          textAlign: isMobile ? 'center' : 'left',
        }}
      >
        {/* Registered address + support inbox. These plain details are a large
            part of what makes the company read as real, per the owner note. */}
        <p style={{ margin: 0, fontFamily: FONTS.body, fontSize: '12px', color: FILLS.textMuted }}>
          Belhop, Inc. <span style={{ color: FILLS.goldAlpha30 }}>&middot;</span> 319 Aspen Airport
          Business Center, Aspen, Colorado{' '}
          <span style={{ color: FILLS.goldAlpha30 }}>&middot;</span>{' '}
          <a
            className="footer-link"
            href={`mailto:${SUPPORT_EMAIL}`}
            style={{ fontFamily: FONTS.body, fontSize: '12px', textDecoration: 'none' }}
          >
            {SUPPORT_EMAIL}
          </a>
        </p>
        <p
          style={{
            margin: '8px 0 0',
            fontFamily: FONTS.body,
            fontSize: '12px',
            color: FILLS.textMuted,
          }}
        >
          &copy; {new Date().getFullYear()} Belhop, Inc. All rights reserved.
          {Object.values(FOOTER_PRIVACY_LABELS).map((label) => (
            <React.Fragment key={label}>
              {' '}
              <span style={{ whiteSpace: 'nowrap' }}>
                <span style={{ color: FILLS.goldAlpha30 }}>&middot;</span>{' '}
                <button
                  type="button"
                  className="footer-link"
                  style={legalLineButtonStyle}
                  onClick={openPreferences}
                >
                  {label}
                </button>
              </span>
            </React.Fragment>
          ))}
        </p>
      </div>
    </footer>
  );
};

export default Footer;
